import { z } from 'zod'
import { BASIS_POINTS_FULL } from './money'

export type EntityKind = 'person' | 'company'

const DANISH: ReadonlyArray<readonly [RegExp, string]> = [
  [/[æÆ]/g, 'ae'],
  [/[øØ]/g, 'oe'],
  [/[åÅ]/g, 'aa'],
]

const MAX_KEY_LENGTH = 32

/** What an entity named in characters this leaves nothing of is filed under. */
const UNNAMEABLE = 'entity'

/**
 * The key a newly named entity is filed under.
 *
 * Derived rather than asked for, and then never derived again: the key is what
 * every account's attribution, the valuation path and a hand-written
 * `NORDNET_ACCOUNT_ENTITIES` all name, so recomputing it when the name is
 * edited would re-attribute money that has already been collected under it. The
 * name is the label and is free to change; this is the identity and is not.
 *
 * Lower case rather than `slugSymbol`'s upper, because the keys already in the
 * wild are lower case and one of the places a key is written is an environment
 * variable somebody types by hand.
 */
export function deriveEntityKey(name: string): string {
  let key = name
  for (const [pattern, replacement] of DANISH) key = key.replace(pattern, replacement)
  key = key
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_KEY_LENGTH)
    .replace(/-+$/g, '')

  return key === '' ? UNNAMEABLE : key
}

export const RETIRED_ENTITIES_SETTING_KEY = 'retiredEntities'

export const entityRetirementSchema = z.object({
  entityKey: z.string().min(1),
  retiredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
})

export interface EntityRetirement {
  /**
   * The key rather than the row id, because the key is what outlives a rename
   * and what the attribution menus this list filters are built from.
   */
  entityKey: string
  /** The date it stopped being offered. Nothing already attributed to it moves. */
  retiredOn: string
}

/** A malformed setting must not take the attribution menus down with it. */
export function parseEntityRetirements(value: unknown): EntityRetirement[] {
  const parsed = z.array(entityRetirementSchema).safeParse(value ?? [])
  return parsed.success ? parsed.data : []
}

export interface EntityNode {
  key: string
  name: string
  kind: EntityKind
}

export interface OwnershipEdge {
  ownerKey: string
  ownedKey: string
  shareBp: number
}

export interface OwnershipGraph {
  entities: ReadonlyMap<string, EntityNode>
  ownersOf: ReadonlyMap<string, readonly OwnershipEdge[]>
}

export class OwnershipError extends Error {}

export function buildOwnershipGraph(
  entities: readonly EntityNode[],
  edges: readonly OwnershipEdge[],
): OwnershipGraph {
  const byKey = new Map<string, EntityNode>()
  for (const node of entities) {
    if (byKey.has(node.key)) throw new OwnershipError(`duplicate entity key: ${node.key}`)
    byKey.set(node.key, node)
  }

  const ownersOf = new Map<string, OwnershipEdge[]>()
  for (const edge of edges) {
    if (!byKey.has(edge.ownerKey)) throw new OwnershipError(`unknown owner: ${edge.ownerKey}`)
    if (!byKey.has(edge.ownedKey)) throw new OwnershipError(`unknown owned entity: ${edge.ownedKey}`)
    if (!Number.isInteger(edge.shareBp) || edge.shareBp < 0 || edge.shareBp > BASIS_POINTS_FULL) {
      throw new OwnershipError(`share_bp out of range for ${edge.ownerKey}->${edge.ownedKey}: ${edge.shareBp}`)
    }
    const list = ownersOf.get(edge.ownedKey) ?? []
    list.push(edge)
    ownersOf.set(edge.ownedKey, list)
  }

  for (const [ownedKey, list] of ownersOf) {
    const total = list.reduce((sum, edge) => sum + edge.shareBp, 0)
    if (total > BASIS_POINTS_FULL) {
      throw new OwnershipError(`${ownedKey} is owned ${total}bp, more than 100%`)
    }
  }

  return { entities: byKey, ownersOf }
}

/**
 * Who ultimately holds an entity, in basis points, following the graph up until
 * it reaches entities nobody owns. A holding company that is only 60% owned
 * keeps the remaining 40% of itself, so shares always total 10000.
 */
export function ultimateOwnership(graph: OwnershipGraph, entityKey: string): Map<string, number> {
  if (!graph.entities.has(entityKey)) throw new OwnershipError(`unknown entity: ${entityKey}`)

  const shares = new Map<string, number>()

  const walk = (key: string, shareBp: number, seen: readonly string[]): void => {
    if (seen.includes(key)) {
      throw new OwnershipError(`ownership cycle: ${[...seen, key].join(' -> ')}`)
    }
    const owners = graph.ownersOf.get(key) ?? []
    const ownedBp = owners.reduce((sum, edge) => sum + edge.shareBp, 0)
    const selfBp = BASIS_POINTS_FULL - ownedBp
    if (selfBp > 0) {
      shares.set(key, (shares.get(key) ?? 0) + scale(shareBp, selfBp))
    }
    for (const edge of owners) {
      walk(edge.ownerKey, scale(shareBp, edge.shareBp), [...seen, key])
    }
  }

  walk(entityKey, BASIS_POINTS_FULL, [])
  return shares
}

export function shareOf(graph: OwnershipGraph, ownerKey: string, entityKey: string): number {
  return ultimateOwnership(graph, entityKey).get(ownerKey) ?? 0
}

/**
 * The entities nobody owns. Every share the graph attributes ends at one of
 * these, so they are the only entities a household's headline figures can be
 * computed for: anyone further down is already counted through the owner above.
 */
export function rootEntities(graph: OwnershipGraph): EntityNode[] {
  return [...graph.entities.values()].filter(
    (node) => (graph.ownersOf.get(node.key) ?? []).length === 0,
  )
}

function scale(shareBp: number, byBp: number): number {
  return Math.round((shareBp * byBp) / BASIS_POINTS_FULL)
}
